import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { getProducts, type BrokerProduct } from "@/lib/brokerProducts";
import { ExchangeHero } from "@/components/ExchangeHero";
import { newSubmissionPath } from "@/lib/submissions/submissionPaths";

import { buttonClass } from "@/components/buttonStyles";
import { Plus } from "lucide-react";
// The API Exchange — the post-login landing (BRK-S021). Leads with the
// submission CTA and shows the product marketplace, SERVER-DRIVEN from the PMS
// product catalog (GET /api/broker/products — BRK-S051/PMS-S141). The shell
// chrome (nav, identity, sign-out) is the surrounding PortalShell (BRK-S035);
// this page is just the Exchange content on the light Exchange canvas.
//
// Each product card starts a submission for that product (BRK-S311): it opens
// its line's own URL (/submissions/wc/new, /submissions/gl/new), else the shared
// /submissions/new.
//
// The "Direct Markets" carrier row is presentational-only scaffolding (dummy
// cards) for eventual direct-appointment integrations — it links to nothing yet.

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-heading text-brk-text">{children}</h2>
      {/* Presentational-only (dummy). */}
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-brk-accent transition-colors hover:text-brk-accent-strong"
      >
        View all <span aria-hidden="true">›</span>
      </button>
    </div>
  );
}

export function PortalPage() {
  const { authedRequest } = useAuth();
  const [products, setProducts] = useState<BrokerProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { items } = await getProducts(authedRequest);
        if (!cancelled) setProducts(items);
      } catch {
        // A failed catalog read degrades just the grid — the CTA stays (NN #7).
        if (!cancelled)
          setError("Couldn’t load the marketplace — reload to try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  return (
    <div className="min-h-full bg-brk-bg p-4 text-brk-text sm:p-6">
      <div className="flex flex-col gap-10">
        <ExchangeHero
          eyebrow="API Exchange"
          title="Get your risk to market in minutes"
          subtitle="Start a submission and we’ll route you to the right markets."
          actions={
            <Link to="/submissions/new" className={buttonClass()}>
              <Plus aria-hidden size={16} />
              Start submission
            </Link>
          }
        />

        {/* Marketplace — server-driven product catalog. */}
        <section className="flex flex-col gap-4">
          <SectionHeading>Exchange Products</SectionHeading>
          {error && <p className="text-sm text-brk-dim">{error}</p>}
          {!error && products === null && (
            <p role="status" className="text-sm text-brk-dim">
              Loading the marketplace…
            </p>
          )}
          {products && products.length === 0 && (
            <p className="text-sm text-brk-dim">
              No products are live right now — check back soon.
            </p>
          )}
          {products && products.length > 0 && (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <li key={p.id} className="flex">
                  <Link
                    to={newSubmissionPath(p.lineOfBusiness)}
                    aria-label={`Start a ${p.name} submission`}
                    className="group flex w-full flex-col gap-6 rounded-2xl border border-brk-border bg-brk-panel p-5 transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brk-accent/10 text-brk-accent"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M4 9h16M4 9l1.5-4.5A1 1 0 0 1 6.45 4h11.1a1 1 0 0 1 .95.68L20 9M4 9v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-400/15 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                        <span aria-hidden="true">●</span> Active
                      </span>
                    </div>
                    <div className="flex items-end justify-between gap-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-brk-text">
                          {p.name}
                        </span>
                        <span className="text-sm text-brk-dim">
                          {p.distributionMode === "marketplace"
                            ? "Multiple markets, one submission"
                            : "Signal-written coverage"}
                        </span>
                      </div>
                      <span
                        aria-hidden="true"
                        className="text-brk-dim transition-colors group-hover:text-brk-accent"
                      >
                        →
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
